import { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function RoleMaster() {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ role_code: "", role_name: "", description: "" });
  const [selected, setSelected] = useState(new Set());
  const [catalog, setCatalog] = useState({ modules: {} });
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/permissions").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm({ role_code: "", role_name: "", description: "" }); setSelected(new Set()); setOpen(true); };
  const openEdit = async (row) => {
    setEditing(row);
    setForm({ role_code: row.role_code, role_name: row.role_name, description: row.description || "" });
    try {
      const { data } = await api.get(`/roles/${row.id}`);
      setSelected(new Set(data.permission_codes || []));
    } catch { setSelected(new Set()); }
    setOpen(true);
  };

  const toggleCode = (code) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  };

  const toggleModule = (mod, allCodes) => {
    setSelected((s) => {
      const n = new Set(s);
      const allSelected = allCodes.every((c) => n.has(c));
      allCodes.forEach((c) => (allSelected ? n.delete(c) : n.add(c)));
      return n;
    });
  };

  const save = async () => {
    if (!form.role_code.trim() || !form.role_name.trim()) {
      toast.error("Role code and name are required");
      return;
    }
    try {
      const codes = Array.from(selected);
      if (editing) {
        await api.put(`/roles/${editing.id}`, { role_name: form.role_name, description: form.description, permission_codes: codes });
        toast.success("Role updated");
      } else {
        await api.post("/roles", { ...form, permission_codes: codes });
        toast.success("Role created");
      }
      setOpen(false);
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggleStatus = async (row) => {
    try {
      await api.patch(`/roles/${row.id}/status?is_active=${!row.is_active}`);
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const columns = [
    { key: "role_code", label: "Code", mono: true },
    { key: "role_name", label: "Role", render: (r) => <span className="font-medium text-slate-900">{r.role_name}</span> },
    { key: "permission_count", label: "Permissions", render: (r) => <Badge variant="secondary">{r.permission_count} perms</Badge> },
    { key: "is_system", label: "Type", render: (r) => r.is_system ? <Badge className="bg-slate-200 text-slate-600">System</Badge> : <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Custom</Badge> },
    { key: "status", label: "Status", render: (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="Role Master" subtitle="Define roles and module/action permissions" actions={
        has("ROLE.CREATE") && <Button data-testid="role-add" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New Role</Button>
      } />

      <DataTable
        testid="roles-table"
        columns={columns}
        searchPlaceholder="Search roles..."
        refreshKey={refresh}
        load={(params) => api.get("/roles", { params }).then((r) => r.data)}
        rowActions={(row) => (
          <div className="flex justify-end gap-1">
            {has("ROLE.EDIT") && <Button data-testid={`role-edit-${row.id}`} variant="ghost" size="sm" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>}
            {has("ROLE.DEACTIVATE") && !row.is_system && <Button data-testid={`role-toggle-${row.id}`} variant="ghost" size="sm" onClick={() => toggleStatus(row)}><Power className={`h-4 w-4 ${row.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>}
          </div>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Role Code *</Label><Input data-testid="role-code-input" disabled={!!editing} value={form.role_code} onChange={(e) => setForm({ ...form, role_code: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2"><Label>Role Name *</Label><Input data-testid="role-name-input" value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>

          <div className="mt-2">
            <div className="mb-2 text-sm font-semibold text-slate-700">Permission Matrix</div>
            <div className="space-y-4" data-testid="rbac-matrix">
              {Object.entries(catalog.modules).map(([mod, perms]) => {
                const codes = perms.map((p) => p.permission_code);
                const allSelected = codes.every((c) => selected.has(c));
                return (
                  <div key={mod} className="rounded-lg border border-slate-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-display text-sm font-semibold text-slate-800">{mod}</span>
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                        <Checkbox data-testid={`rbac-module-${mod.replace(/\s+/g, "-").toLowerCase()}`} checked={allSelected} onCheckedChange={() => toggleModule(mod, codes)} /> Select all
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {perms.map((p) => (
                        <label key={p.permission_code} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                          <Checkbox data-testid={`rbac-perm-${p.permission_code}`} checked={selected.has(p.permission_code)} onCheckedChange={() => toggleCode(p.permission_code)} />
                          <span className="text-slate-700">{p.action}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="role-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
